import {OwayoJSKonfiViewer} from "./owayoJSViewerWithSelObs";
import {PortalLine} from "./portallinie";
import {UtilityPlane} from "./utilityPlane";
import {PartSelectableObject} from "./partSelOb";
import {MathUtils} from "./mathUtils";
import {GreyOutState} from "./owayoPartWithSelObs";
import {OwayoKonfiPart} from "./owayoPartWithSelObs";
import {Snap} from "./snap";
import {CONSTANTS} from "./meta_data";
import {Vector2, WidthHeight} from "./delme_globals";
import {THREE} from "./resource/BVH.js";
import globalsObject from "./globalsObject";
import {SelObTypes} from "./SelObTypes";
import {arrayIndexByKeyValue} from "../global/arrayHelper";




export class GlobalUtilityPainter {
    private parent:OwayoJSKonfiViewer;
    nonEmptyLayers:UtilityPlane[] = [];
    isFadingOut = false;
    fadingAlpha=1;
    fadeOutIntervallID =-1;
    snaps:Snap[]=[];
    activeSnaps:number[]=[];
    sortedSnaps={
        horizontal: [] as Snap[],
        vertical: [] as Snap[],
        multiLine: [] as Snap[]
    };

    constructor(parent) {
        this.parent = parent;
    }

    clearActiveSnaps() {
        this.activeSnaps = [];
        this.sortedSnaps.horizontal = [];
        this.sortedSnaps.multiLine = [];
        this.sortedSnaps.vertical = [];
    }

    paintSelectionBorders() {
        if (this.parent.selectedObject === null) {
            return;
        }
        this.paintObjectBorder(this.parent.selectedObject.id, this.parent.selectedObject.isConflicted());
        for (let i = 0, len = this.parent.entangledObjects.length; i < len; i++) {
            this.paintObjectBorder((this.parent.entangledObjects)[i].id, (this.parent.entangledObjects)[i].isConflicted());
        }
    }

    paintObjectBorder(id, hasErrorBorder = false) {
        if (typeof(hasErrorBorder) === "undefined") {
            hasErrorBorder = false;
        }
        const element = this.parent.getPartSelObById(id); //damit wird auch "part" abgefangen (returnwert null)

        if (element === null) {
            return;
        }

        const extremes = element.getPartCanvasMinMaxCoordinates();

        const layer = element.getParent().handleAndGuideLayer;

        if (layer.anzRectangles < layer.rectangles.length) {
            layer.rectangles[layer.anzRectangles].x = extremes.minX;
            layer.rectangles[layer.anzRectangles].y = extremes.maxY;
            layer.rectangles[layer.anzRectangles].z = extremes.maxX;
            layer.rectangles[layer.anzRectangles].w = extremes.minY;
            layer.rectangleColors[layer.anzRectangles] = (hasErrorBorder ? 1 : 0);
            layer.parent.handleShader.uniforms.rectangles.value[layer.anzRectangles].x = extremes.minX;
            layer.parent.handleShader.uniforms.rectangles.value[layer.anzRectangles].y = extremes.maxY;
            layer.parent.handleShader.uniforms.rectangles.value[layer.anzRectangles].z = extremes.maxX;
            layer.parent.handleShader.uniforms.rectangles.value[layer.anzRectangles].w = extremes.minY;
            layer.parent.handleShader.uniforms.rectangleColors.value[layer.anzRectangles] = (hasErrorBorder ? 1 : 0 );

            layer.anzRectangles++;
            layer.parent.handleShader.uniforms.anzRectangles.value = layer.anzRectangles;
        }
        this.addToNonEmptyLayers(layer);
    }

    private static getSnappedByPartResult(snap:Snap):{minX, maxX, minY, maxY} {
        return {
            minX: Math.min(snap.points[0].x, snap.points[1].x),
            maxX: Math.max(snap.points[0].x, snap.points[1].x),
            minY: Math.min(snap.points[0].y, snap.points[1].y),
            maxY: Math.max(snap.points[0].y, snap.points[1].y)
        };
    }

    private static getMaxWithinBoundary(resultMax, extremesMax, valueUpperBoundary) {
        return isNaN(resultMax) ? Math.min(valueUpperBoundary, extremesMax) :
            Math.min(valueUpperBoundary, Math.max(resultMax, extremesMax));
    }

    private static getMinGreaterZero(resultMin, extremesMin) {
        return isNaN(resultMin) ? Math.max(0, extremesMin) : Math.max(0, Math.min(resultMin, extremesMin));
    }

    private isSnapToClone(snap, partSelOb) {
        for (let j = 0, lenJ = snap.snappedBy.length; j < lenJ; j++) {
            const compareSelOb = this.parent.getPartSelObById(snap.snappedBy[j]) as PartSelectableObject;
            if (compareSelOb.getParentPartName() === snap.partName && partSelOb.cloneIds.indexOf(compareSelOb.id) > -1) {
                return true;
            }
        }
        return false;
    }

    private static getIntersectionWithPortallines(snap:Snap, portalLines: PortalLine[]) {
        for (let j = 0, lenJ = portalLines.length; j < lenJ; j++) {
            if (portalLines[j].presentOnPart(snap.partName)) {
                return  portalLines[j].intersectionPoint(snap);
            }
        }
        return null;
    }

    private static adjustExtremes(oldExtremes:{minX, maxX, minY, maxY}, newValues:{minX, maxX, minY, maxY}, valueUpperBoundaries:Vector2) {
        oldExtremes.maxX = GlobalUtilityPainter.getMaxWithinBoundary(oldExtremes.maxX, newValues.maxX, valueUpperBoundaries.x);
        oldExtremes.minX = GlobalUtilityPainter.getMinGreaterZero(oldExtremes.minX, newValues.minX);
        oldExtremes.maxY = GlobalUtilityPainter.getMaxWithinBoundary(oldExtremes.maxY, newValues.maxY, valueUpperBoundaries.y);
        oldExtremes.minY = GlobalUtilityPainter.getMinGreaterZero(oldExtremes.minY, newValues.minY);
        return oldExtremes;
    }

    getSnapExtremes(snap):{minX, maxX, minY, maxY} {
        if(snap.snappedBy.some(s=>s==="part")) {
            return GlobalUtilityPainter.getSnappedByPartResult(snap);
        }

        const textureSize = this.parent.modelParts[snap.partName].textureSize;
        let result = {minX: NaN, maxX: NaN, minY: NaN, maxY: NaN};
        for (let i = 0, lenI = snap.snappedBy.length; i < lenI; i++) {
            const partSelOb = this.parent.getPartSelObById(snap.snappedBy[i]) as PartSelectableObject;
            if (snap.isSelfSnap(partSelOb)) {
                continue;
            }
            const extremes = partSelOb.getPartCanvasMinMaxCoordinates();
            if (partSelOb.getParentPartName() === snap.partName) {
                //wir haben einen Snap, der nicht nur Klon und Objekt betrifft!
                result = GlobalUtilityPainter.adjustExtremes(result, extremes, textureSize);
            } else {
                //wir haben einen geklonten Snap. Der muss also bis zur Portallinie gehen.
                //Vorsicht! Hatte hier einen Denkfehler. Wir zeichnen gerade den Snap auf dem Part, der durch den Snap vorgegeben wird,
                //nicht auf dem Part des partSelOb, dass wir gerade behandeln!
                //weiterer Denkfehler: Auch wenn nicht nur Objekt+Klon auf dem Snap liegen, darf ich nie zwischen Klon und Objekt eine Snaplinie zeichnen
                //wenn also z.B. zwei Objekte und ein Klon des einen Objekts auf dem Snap liegen, dann zeichne nur zwischen den beiden Objekten!
                //ich checke jetzt also, ob das Objekt auf das ich auf dem anderen Part snappen möchte Klon eines Objektes auf dem Ausgangspart ist
                if(this.isSnapToClone(snap, partSelOb)) {
                    continue;
                }
                const intersection = GlobalUtilityPainter.getIntersectionWithPortallines(snap, this.parent.portalLines);
                if (intersection === null) {
                    //FIXME das passiert manchmal! Habe gerade keine Ahnung warum; Wahrscheinlich: wenn Partsnaps auf dem anderen Part liegen...
                    //console.warn("intersection war null!");
                    break;
                    //return;
                }
                result = GlobalUtilityPainter.adjustExtremes(
                    result,
                    {minX:intersection.x, maxX:intersection.x, minY:intersection.y, maxY:intersection.y},
                    textureSize
                );
            }
        }
        return result;
    }

    /**
     * Zeichnet auf dem Layer eine Verticale Linie.
     * VORSICHT! Diese Funktion verändert automatisch die Varibalen anzVerticalLines in shader und layer!
     */
    private paintVerticalLine(snap:Snap, layer:UtilityPlane, uniforms) {
        const vector = GlobalUtilityPainter.getVerticalVector(snap, this.getSnapExtremes(snap));
        layer.verticalLines[layer.anzVerticalLines] = vector;
        uniforms.verticalLines.value[layer.anzVerticalLines] = vector;
        layer.anzVerticalLines++;
        uniforms.anzVerticalLines.value = layer.anzVerticalLines;
    }

    /**
     * Zeichnet auf dem Layer eine Horizontale Linie.
     * VORSICHT! Diese Funktion verändert automatisch die Varibalen anzVerticalLines in shader und layer!
     */
    private paintHorizontalLine(snap:Snap, layer:UtilityPlane, uniforms) {
        const vector = GlobalUtilityPainter.getHorizontalVector(snap, this.getSnapExtremes(snap));
        layer.horizontalLines[layer.anzHorizontalLines] = vector;
        uniforms.horizontalLines.value[layer.anzHorizontalLines] = vector
        layer.anzHorizontalLines++;
        uniforms.anzHorizontalLines.value = layer.anzHorizontalLines;
    }

    private static getHorizontalVector(snap:Snap, extremes:{minX, maxX, minY, maxY}) {
        return new THREE.Vector3(snap.points[0].y, extremes.minX, extremes.maxX);
    }

    private static getVerticalVector(snap:Snap, extremes:{minX, maxX, minY, maxY}) {
        return new THREE.Vector3(snap.points[0].x, extremes.minY, extremes.maxY);
    }

    paintSnap(snap) {
        const part = this.parent.modelParts[snap.partName];
        const uniforms = part.handleShader.uniforms;
        const layer = part.handleAndGuideLayer;

        switch (snap.category) {
            case "vertical":
                this.paintVerticalLine(snap, layer, uniforms);
                break;
            case "horizontal":
                this.paintHorizontalLine(snap, layer, uniforms);
                break;
            case "multiLine":
                for (let i = 1, lenI = snap.points.length; i < lenI; i++) {
                    layer.anzMatrices++;
                    uniforms.anzMatrices.value = layer.anzMatrices;
                    uniforms.matrices.value[layer.anzMatrices - 1] = snap.getMatrixForPoints(i - 1, i);
                }
                break;
            default:
                globalsObject.controller.handleError("internal", "paintSnap", "unknown Snap Category " + snap.category);
        }
        this.addToNonEmptyLayers(layer);
    }

    clearLayer(layer) {
        layer.anzRectangles = 0;
        layer.parent.handleShader.uniforms.anzRectangles.value = 0;
        layer.anzMatrices = 0;
        layer.anzHorizontalLines = 0;
        layer.anzVerticalLines = 0;
        layer.parent.handleShader.uniforms.anzMatrices.value = 0;
        layer.parent.handleShader.uniforms.anzHorizontalLines.value = 0;
        layer.parent.handleShader.uniforms.anzVerticalLines.value = 0;
        this.removeFromNonEmptyLayersIfPresent(layer);
    }

    public removeFromNonEmptyLayersIfPresent(layer:UtilityPlane) {
        for (let i = this.nonEmptyLayers.length - 1; i > -1; i--) {
            if (this.nonEmptyLayers[i] === layer) {
                this.nonEmptyLayers.splice(i, 1);
                return;
            }
        }
    }

    public addToNonEmptyLayers(layer:UtilityPlane) {
        if (this.nonEmptyLayers.indexOf(layer) === -1) {
            this.nonEmptyLayers.push(layer);
        }
    }


    /**
     * Fadet die Smartguides aus, indem er (und das geht bestimmt schöner) den gesamten Layer neu zeichnet und vorher den globalen
     * Alpha Wert des Canvas (quadratisch) runterschraubt. Die Umrandung für das ausgewählte Objekt setzt beim Zeichnen für sich
     * selbst immer einen festen Alpha Wert und wird dadurch nicht mitgefaded.
     * Diese Funktion ruft sich selbst mittels setTimeOut auf.
     * @param {number}  [stepCounter = 0]       zählt mit, wie oft die Funktion schon aufgerufen wurde für die Berechnung
     */
    fadeOut(stepCounter = 0) {
        if (this.nonEmptyLayers.length === 0) {
            this.isFadingOut = false;
            return;
        }

        if (typeof(stepCounter) === "undefined") {
            stepCounter = 0;
            this.fadingAlpha = 1;
        }

        this.isFadingOut = true;
        if (this.fadingAlpha <= 0.1) {
            this.fadingAlpha = 0;
            //noch einen Durchlauf
        } else {
            this.fadingAlpha =
                -(1 / Math.pow(CONSTANTS.HELPER_FADEOUT_STEPS, 2)) * Math.pow(stepCounter, 2) + 1;
            stepCounter += 1;
        }
        for (let i = 0, len = this.nonEmptyLayers.length; i < len; i++) {
            this.nonEmptyLayers[i].parent.handleShader.uniforms.textureAlpha.value = this.fadingAlpha;
        }

        this.parent.render();
        if (this.fadingAlpha <= 0) {
            this.finishFadeOut();
            return;
        }
        this.fadeOutIntervallID = setTimeout(()=>this.fadeOut(stepCounter), 50) as unknown as number;
    }

    /**
     * Im Gegensatz zu finitshFadeout: Smartguides werden nicht gelöscht sondern wieder angezeigt.
     * Setzt das Ausfaden zurück auf globales Alpha 1. Der Timeout wird gelöscht
     */
    resetFading() {

        let i, len;
        clearTimeout(this.fadeOutIntervallID);
        this.isFadingOut = false;
        this.fadingAlpha = 1;
        for (i = 0, len = this.nonEmptyLayers.length; i < len; i++) {
            this.nonEmptyLayers[i].parent.handleShader.uniforms.textureAlpha.value = 1;
        }
        this.parent.render();

    }

    /**
     * das fadeout ist abgeschlossen. Lösche die Smartguides und das timeout
     */
    finishFadeOut(emptyLayers = true) {
        this.isFadingOut = false;
        clearTimeout(this.fadeOutIntervallID);
        if (emptyLayers === false) {
            this.fadingAlpha = 1;
            this.setNonEmptyLayersTextureAlphaToFadingAlpha();
        } else {
            this.fadingAlpha = -1;
            this.setNonEmptyLayersTextureAlphaToFadingAlpha();
        }
        this.parent.render();
    }

    private setNonEmptyLayersTextureAlphaToFadingAlpha() {
        let i, len;
        for (i = 0, len = this.nonEmptyLayers.length; i < len; i++) {
            this.nonEmptyLayers[i].parent.handleShader.uniforms.textureAlpha.value = this.fadingAlpha;
        }
        return {i: i, len: len};
    }

    /**
     * wenn wir bereits im Ausfaden sind: zurücksetzen des Fadens
     * Dann neuer Start. Verzögerung: s. CONSTANTS.HELPER_FADEOUT_START
     */
    restartFading() {

        const $this = this;
        if (this.isFadingOut) {
            this.resetFading();
        }
        this.isFadingOut = true;
        this.fadeOutIntervallID = setTimeout(function () {
            $this.fadeOut();
        }, CONSTANTS.HELPER_FADEOUT_START) as unknown as number;
    }

    /**
     * Alle Objekte, die auf SnapLinien liegen, die Aktiv sind, werden markiert.
     * Problem:
     * Ein Klone eines Objektes könnte entweder ebenfalls auf einer aktiven Snaplinie (die durch die Partgrenze verlängert wurde) liegen oder aber,
     * z.B. Snap zweier Logos links auf links, ein Klon nach rechts über die partgrenze, der Klon selbst liegt nicht auf einer Snaplinie.
     *
     *
     */
    markSnappedObjects() {


        let i, lenI, snappedBy, alreadyPainted = [];

        for (i = 0, lenI = this.activeSnaps.length; i < lenI; i++) {
            snappedBy = this.snaps[this.activeSnaps[i]].snappedBy;
            alreadyPainted = this.markSnappedObjectsBySnappedBy(snappedBy, alreadyPainted);
        }
    }

    private markSnappedObjectsBySnappedBy(snappedBy, alreadyPainted) {
        let i, len;
        for (i = 0, len = snappedBy.length; i < len; i++) {
            if (snappedBy[i].indexOf("part") === -1 && alreadyPainted.indexOf(snappedBy[i]) === -1) {
                this.paintObjectBorder(snappedBy[i]);
                alreadyPainted.push(snappedBy[i]);
                alreadyPainted = this.paintObjectBordersOnClones(this.parent.getPartSelObById(snappedBy[i]), alreadyPainted);
            }
        }
        return alreadyPainted;
    }

    private paintObjectBordersOnClones(element, alreadyPainted) {
        let i, len;
        for (i = 0, len = element.cloneIds.length; i < len; i++) {
            if (alreadyPainted.indexOf(element.cloneIds[i]) === -1) {
                this.paintObjectBorder(element.cloneIds[i]);
                alreadyPainted.push(element.cloneIds[i]);
            }
        }
        return alreadyPainted;
    }

    clearAllLayers = ()=>{
        for(let i=this.nonEmptyLayers.length-1; i>-1; i--) {
            this.clearLayer(this.nonEmptyLayers[i]);
        }
    }

    paintActiveSnapLines() {
        this.paintSnaps(this.sortedSnaps.horizontal);
        this.paintSnaps(this.sortedSnaps.vertical);
        this.paintSnaps(this.sortedSnaps.multiLine);
    }

    private paintSnaps(snapArray: Snap[]) {
        let i, lenI;
        for (i = 0, lenI = snapArray.length; i < lenI; i++) {
            this.paintSnap(snapArray[i]);
        }
    }


    /**
     * Alle Informationen, die aktuell auf HandleAndGuideLayern dargestellt werden sollen werden neu gezeichnet
     */
    repaint() {

        let i, len;
        this.clearAllLayers();
        this.paintSelectionBorders();
        this.setActiveSnaps();
        this.paintActiveSnapLines();
        this.markSnappedObjects();

        if (this.parent.selectedObject !== null) {
            this.addToNonEmptyLayers(this.parent.selectedObject.getParent().handleAndGuideLayer);
        }
        for (i = 0, len = this.parent.entangledObjects.length; i < len; i++) {
            this.addToNonEmptyLayers(this.parent.entangledObjects[i].getParent().handleAndGuideLayer);
        }
    }

    static snapShouldBePainted(snap, part) {
        let i, len, shouldPaint = false;
        for (i = 0, len = snap.snappedBy.length; i < len; i++) {
            if (arrayIndexByKeyValue(part.selectableObjects, "id", snap.snappedBy[i])) {
                shouldPaint = true;
            }
        }
        return shouldPaint;
    }

    pushSnapToSortedSnapsAccordingToCategory(snap) {
        switch (snap.category) {
            case "horizontal":
                this.sortedSnaps.horizontal.push(snap);
                break;
            case "vertical":
                this.sortedSnaps.vertical.push(snap);
                break;
            default:
                this.sortedSnaps.multiLine.push(snap);
        }
    }
    calculateSnapsOfSelOb(currentSelOb) {
        let i, len, part, snap;
        for (i = 0, len = this.snaps.length; i < len; i++) {
            snap = this.snaps[i];
            //wenn dieser Snap vom partSelOb und mindestens einem weiteren belegt ist...
            if (snap.snappedBy.indexOf(currentSelOb.id) === -1 || snap.isSelfSnap(currentSelOb)) {
                continue;
            }

            part = this.parent.modelParts[snap.partName];
            if (GlobalUtilityPainter.snapShouldBePainted(snap, part)) {
                this.activeSnaps.push(i);
                this.pushSnapToSortedSnapsAccordingToCategory(snap);
            }
        }
    }

    /**
     * setzt die activen Snaps für ein PartSelectableObject. D.h. es checkt die aktuelle Position und prüft,
     * welche Snaps mit den Koordinaten übereinstimmen.
     * Hier wird also <strong>nicht</strong> die Position verändert!
     */
    setActiveSnaps() {

        let i, lenI, currentSelOb;

        if (this.parent.selectedObject === null) {
            return;
        }
        const partSelObsToBeMarked = this.parent.entangledObjects.slice(0);
        partSelObsToBeMarked.push(this.parent.selectedObject);

        this.activeSnaps = [];
        this.emptySortedSnaps();


        for (i = 0, lenI = partSelObsToBeMarked.length; i < lenI; i++) {
            currentSelOb = partSelObsToBeMarked[i];
            this.calculateSnapsOfSelOb(currentSelOb);
        }
        this.sortedSnaps.horizontal.sort(function (a, b) {
            return a.points[0].y - b.points[0].y;
        });
        this.sortedSnaps.vertical.sort(function (a, b) {
            return a.points[0].x - b.points[0].x;
        });
    }


    /**
     * Leert das Object this.sortedSnaps
     */
    emptySortedSnaps() {

        this.sortedSnaps.horizontal = [];
        this.sortedSnaps.vertical = [];
        this.sortedSnaps.multiLine = [];

    }

    /**
     * Für ein selektierbares Objekt werden die Snaps (Mitte und die 4 Kanten) eingetragen
     *
     * @param {PartSelectableObject} partSelOb
     * @param {boolean} [isClone=false]
     */
    addSnapsForSelOb(partSelOb:PartSelectableObject, isClone = false) {
        if (typeof(isClone) === "undefined") {
            isClone = false;
        }
        if (partSelOb.floating === CONSTANTS.FLOAT_BOTTOM) {
            return;
        }
        let i, len, clone;

        const extremes = partSelOb.getPartCanvasMinMaxCoordinates();
        const values = GlobalUtilityPainter.getSnapValuesForPartSelOb(partSelOb, extremes);

        this.registerObjectToObjectSnaps(partSelOb.getParentPartName(), values, partSelOb.id);
        this.parent.cloneObjectToObjectSnaps(partSelOb.getParentPartName(), values, partSelOb.id); // Portallinien klonen auch Snaps!

        this.registerObjectToPartSnaps(partSelOb.translation, partSelOb.id);
        if (!isClone) {
            for (i = 0, len = partSelOb.cloneIds.length; i < len; i++) {
                clone = this.parent.getPartSelObById(partSelOb.cloneIds[i]);
                this.addSnapsForSelOb(clone, true);
            }
        }
    }

    static getSnapValuesForPartSelOb(partSelOb:PartSelectableObject, extremes) {
        return [
            {
                points: [
                    {x: partSelOb.translation.x, y: 0},
                    {x: partSelOb.translation.x, y: partSelOb.getParent().textureSize.y}
                ],
                edge: "middle",
                category: "vertical"
            },
            {
                points: [
                    {x: extremes.minX, y: 0},
                    {x: extremes.minX, y: partSelOb.getParent().textureSize.y}
                ],
                edge: "left",
                category: "vertical"
            },
            {
                points: [
                    {x: extremes.maxX, y: 0},
                    {x: extremes.maxX, y: partSelOb.getParent().textureSize.y}
                ],
                edge: "right",
                category: "vertical"
            },
            {
                points: [
                    {x: 0, y: partSelOb.translation.y},
                    {x: partSelOb.getParent().textureSize.x, y: partSelOb.translation.y}
                ],
                edge: "middle",
                category: "horizontal"
            },
            {
                points: [
                    {x: 0, y: extremes.minY},
                    {x: partSelOb.getParent().textureSize.x, y: extremes.minY}
                ],
                edge: "top",
                category: "horizontal"
            },
            {
                points: [
                    {x: 0, y: extremes.maxY},
                    {x: partSelOb.getParent().textureSize.x, y: extremes.maxY}
                ],
                edge: "bottom",
                category: "horizontal"
            }
        ];
    }

    /**
     * Löscht die Snap Einträge für das Object mit der id "id"
     * @param {string}  id          id des SelectableObjects, das aus den Snaps entfernt wird
     * @param {string}  [paramString] momentan nur genutzt für "notPortalLines"
     */
    removeSnaps(id, paramString?) {
        this.snaps = this.removeSnapsFromArray(id, paramString);
    }

    changeSnapsForSelOb(partSelOb:PartSelectableObject) {
        this.removeSnaps(partSelOb.id);
        this.parent.removeClonedSnaps(partSelOb.getParentPartName(), partSelOb.id);
        this.addSnapsForSelOb(partSelOb);

    }

    /**
     * checkt ob ein Objekt auf einem Partsnap liegt (d.h. es wird nicht dahin verschoben!!) und trägt die id des Objekts in snappedBy ein
     */
    registerObjectToPartSnaps(point:Vector2, id:string) {
        const partName = (this.parent.getPartSelObById(id) as PartSelectableObject).getParentPartName();
        for (let i = 0; i < this.snaps.length; i++) {
            if (this.snaps[i].snappedBy.indexOf("part") > -1 && this.snaps[i].partName === partName) {
                GlobalUtilityPainter.registerObjectToPartSnapInSnap(point, id, this.snaps[i])
            }
        }
    }

    private static registerObjectToPartSnapInSnap(point, id, snap:Snap) {
        for (let i = 1; i < snap.points.length; i++) {
            if (snap.snappedBy.indexOf(id) === -1 && MathUtils.distToSegment(point, snap.points[i - 1], snap.points[i]).distance < 0.1) {
                snap.snappedBy.push(id);
                return;
            }
        }
    }

    /**
     *
     * Werte werden hinzugefügt. Ist der value bereits vorhanden, wird nur die id in snappedBy eingehängt, ansonsten wird ein neues
     * ArrayElement angelegt. Hier werden keine Snaps überprüft, die sich auf den Part beziehen!
     *
     */
    registerObjectToObjectSnaps(partName, values, id) {
        for (let i = values.length - 1; i > -1; i--) {
            const value = values[i];
            const snap = this.getSnappingSnap(partName, value);

            if (snap !== null) { //bereits ein Snap für dieses Edge mit dieser Koordinate vorhanden
                if (snap.snappedBy.indexOf(id) === -1) {
                    snap.snappedBy.push(id);
                }
            } else {
                this.insertNewSnap(value, id, partName);
            }
        }
    }

    private insertNewSnap(value: any, id, partName) {
        this.snaps.push(
            new Snap(
                value.points,
                id,
                value.edge,
                partName,
                value.category,
                value.name
            )
        );
    }

    private getSnappingSnap(partName, value: any) {
        let snap=null as Snap|null, j;
        for (j = 0; j < this.snaps.length; j++) {
            if (this.snaps[j].snappedBy.indexOf("part") === -1 && this.snaps[j].partName === partName) {
                if (value.edge === this.snaps[j].forEdge) {
                    //Beispiel: Snap für x Koordinate points[0] = {x:300, y:0}
                    if (Math.abs(this.snaps[j].points[0].x - value.points[0].x) < 0.01 && Math.abs(this.snaps[j].points[0].y - value.points[0].y) < 0.01) {
                        snap = this.snaps[j];
                        break;
                    }
                }
            }
        }
        return snap;
    }


    /**
     * Löscht alle ID Einträge. Sollte die id der einzige
     * id Eintrag gewesen sein, so wird der ganze SNAP gelöscht (einfach so, SNAP! hehe)
     * @return {Snap[]}
     *
     */
    removeSnapsFromArray(id, paramString) {
        let i, index, sparePortals = false;

        if (paramString === "notPortalLines") {
            sparePortals = true;
        }

        for (i = this.snaps.length - 1; i > -1; i--) {
            if (!(sparePortals && this.snaps[i].name === "PortalLine")) {
                index = this.snaps[i].snappedBy.indexOf(id);
                if (index > -1) { //es gibt pro Snapvalue nur einen Eintrag mit der ID! oder?
                    this.snaps[i].snappedBy.splice(index, 1);
                    if (this.snaps[i].snappedBy.length === 0) {
                        this.snaps.splice(i, 1);
                    }
                }
            }
        }
        return this.snaps;
    }

    snapsForCoordinate(translationCoordinate:Vector2, widthHeight:WidthHeight, id, range, isClone=false):{point, edges, snappedSnaps} {
        let snapResult, combinedResult;
        const result = {point: null as Vector2|null, edges: "", snappedSnaps: [] as Snap[]};

        const partSelOb = this.parent.getPartSelObById(id) as PartSelectableObject;
        const delta = new Vector2(translationCoordinate.x - partSelOb.translation.x, translationCoordinate.y - partSelOb.translation.y);
        const cloneIds = partSelOb.cloneIds;

        if (!isClone) { //dann müssen wir auch die Klone durchgehen, und schauen ob die snappen
            for (let i = 0, len = cloneIds.length; i < len; i++) {
                const clone = this.parent.getPartSelObById(cloneIds[i]) as PartSelectableObject;
                const extremes = clone.getPartCanvasMinMaxCoordinates();
                const cloneSnap = this.snapsForCoordinate(
                    new Vector2(clone.translation.x + delta.x,clone.translation.y + delta.y),
                    new WidthHeight(extremes.maxX - extremes.minX,extremes.maxY - extremes.minY),
                    clone.id,
                    this.parent.zoomController.currentSnapRange(),
                    CONSTANTS.I_AM_A_CLONE
                );
                if (cloneSnap.point !== null) {
                    const cloneSnapDelta = new Vector2(
                        cloneSnap.point.x - (clone.translation.x + delta.x),
                        cloneSnap.point.y - (clone.translation.y + delta.y)
                    )
                    result.point = Vector2.componentAdd(translationCoordinate, cloneSnapDelta);
                    result.edges += cloneSnap.edges;
                    result.snappedSnaps = result.snappedSnaps.concat(cloneSnap.snappedSnaps);
                }
            }
        }

        for (let i = 0, len = this.snaps.length; i < len; i++) {

            if (this.snaps[i].isSelfSnap(partSelOb)) {
                continue;
            }
            //ein SelOb sollte nicht auf sich selbst snappen, was der Fall wäre, wenn in snappedBy nur einn Eintrag mit der
            //id des Objekts wäre

            snapResult = this.snaps[i].snaps((partSelOb.getParentPartName() as string), translationCoordinate, widthHeight, range);

            if (snapResult !== null) {
                //dieser Snap träfe also auf den Punkt zu.
                if (result.point === snapResult.point) { //es hat sich nichts verändert. z.B. hat ein Klon auf die selbe Bottom Linie gesnappt
                    result.snappedSnaps.push(this.snaps[i]);
                } else {
                    if (result.snappedSnaps.length === 0) {
                        //noch kein weiterer Snap vorhanden
                        result.snappedSnaps.push(this.snaps[i]);
                        result.point = snapResult.point;
                    } else {
                        combinedResult = GlobalUtilityPainter.combineSnaps(result.snappedSnaps, snapResult.point, translationCoordinate, widthHeight, this.snaps[i], range);
                        result.snappedSnaps = combinedResult.snappedSnaps;
                        if (combinedResult.point !== null) {
                            result.point = combinedResult.point;
                        }
                        //sonst wurde der Snap verworfen, weil die Prio nicht hoch genug war...
                    }
                }

            } //ende Snapresult !== null

        } // ende for snaps.length

        if (!isClone) {
            for (let i = 0, len = result.snappedSnaps.length; i < len; i++) {
                result.snappedSnaps[i].snappedBy.push(id);
                result.edges += result.snappedSnaps[i].forEdge;
            }
        }
        return result;
    }

    static combineSnaps(snappedSnaps, snapToPoint, translationCoordinate, widthHeight:WidthHeight, snap, range) {

        let intersects, edges, edgeCoordinate;
        const result = {point: null as Vector2|null, snappedSnaps: snappedSnaps};

        const prio = CONSTANTS.SNAP_EDGE_PRIO[snap.forEdge];

        if (snappedSnaps.length === 1) {
            //Es gibt einen weiteren Snap.
            //Zwei Möglichkeiten: entweder, ich kann auf einen Schnittpunkt snappen, oder die höhere Prio
            //überschreibt die niedrigere

            intersects = GlobalUtilityPainter.intersectSnaps(snap, snappedSnaps[0]);
            edges = snappedSnaps[0].forEdge + snap.forEdge;
            edgeCoordinate = GlobalUtilityPainter.centerCoordinateToEdge(translationCoordinate, widthHeight, edges);
            for (let i = 0, len = intersects.length; i < len; i++) {
                if (MathUtils.vectorLength2D(intersects[i], edgeCoordinate) < range) {
                    result.point = GlobalUtilityPainter.edgeCoordinateToCenter(intersects[i], widthHeight, edges);
                    if (CONSTANTS.SNAP_EDGE_PRIO[snappedSnaps[0].forEdge] < prio) {
                        snappedSnaps.push(snap);
                    } else {
                        snappedSnaps.splice(0, 0, snap); //füge als erstes Element ein. auch wenn wir
                        //die gleiche Prio haben! ist ja egal
                    }
                    return result;
                } //Ende if < range

            } //ende for (intersects)
            //wir sind alle Schnittpunkte durchgelaufen und konnten nicht snappen. Let the Prio Battle begin
            if (CONSTANTS.SNAP_EDGE_PRIO[snappedSnaps[0].forEdge] < prio) {
                // snappedSnaps = [snap]; //neues Array. wir haben die höhere Prio
                result.point = snapToPoint;
            } else {
                result.point = null;
            }
            return result;
        } //Ende Snapped Length === 1


        //so, wir haben also bereits auf 2 Snaps gesnappt.

        if (prio <= CONSTANTS.SNAP_EDGE_PRIO[snappedSnaps[1].forEdge]) {
            //verwirf den Snap, wenn er auch unter dem zweiten Snap liegt von der Prio her
            result.point = null;
            return result;
        }


        if (prio > CONSTANTS.SNAP_EDGE_PRIO[snappedSnaps[0].forEdge]) {
            //versuche, mit der ersten Snaplinie zu schneiden
            intersects = GlobalUtilityPainter.intersectSnaps(snap, snappedSnaps[0]);
            edges = snappedSnaps[0].forEdge + snap.forEdge;
            edgeCoordinate = GlobalUtilityPainter.centerCoordinateToEdge(translationCoordinate, widthHeight, edges);

            for (let k = 0; k < intersects.length; k++) {
                if (MathUtils.vectorLength2D(edgeCoordinate, intersects[k]) < range) {
                    result.point = GlobalUtilityPainter.edgeCoordinateToCenter(intersects[k], widthHeight, edges);
                    snappedSnaps.pop(); //entferne letztes Element
                    snappedSnaps.splice(0, 0, snap);
                    return result;
                }
            }
            //kein Snap mit erstem Element
            //versuche mit zweitem Snap zu schneiden
            intersects = GlobalUtilityPainter.intersectSnaps(snap, snappedSnaps[1]);
            edges = snappedSnaps[1].forEdge + snap.forEdge;
            edgeCoordinate = GlobalUtilityPainter.centerCoordinateToEdge(translationCoordinate, widthHeight, edges);

            for (let k = 0; k < intersects.length; k++) {
                if (MathUtils.vectorLength2D(edgeCoordinate, intersects[k]) < range) {
                    result.point = GlobalUtilityPainter.edgeCoordinateToCenter(intersects[k], widthHeight, edges);
                    snappedSnaps[0] = snap;
                    return result;
                }
            }
            //auch kein Schnittpunkt mit dem zweiten Snap
            // snappedSnaps = [snap]; //neues Array, da wir ja nicht mehr auf zwei snappen
            result.point = snapToPoint;
            return result;

        }
        // wir sind also größer von der Prio als dass 2te aber kleiner oder gleich der ersten
        //d.h. unterschiedliche Prios der ausgangs snaps!

        intersects = GlobalUtilityPainter.intersectSnaps(snap, snappedSnaps[0]);
        edges = snappedSnaps[0].forEdge + snap.forEdge;
        edgeCoordinate = GlobalUtilityPainter.centerCoordinateToEdge(translationCoordinate, widthHeight, edges);

        for (let k = 0; k < intersects.length; k++) {
            if (MathUtils.vectorLength2D(edgeCoordinate, intersects[k]) < range) {
                result.point = GlobalUtilityPainter.edgeCoordinateToCenter(intersects[k], widthHeight, edges);
                snappedSnaps[1] = snap;
                return result;
            }
        }
        //wenn kein Schnittpunkt möglich: Da wir kleiner oder gleich der größeren Prio sind und
        //zwei Snaplinien da sind, verwirf den neuen Snap
        result.point = null;
        return result;
    }



    /**
     * zwei Snaplinien werden geschnitten.
     *
     * <strong>Vorsicht</strong>.
     *
     * Gleichheit (übereinander liegenden Strecken) in einem Abschnitt ergibt <strong>keinen</strong> Schnittpunkt!
     */
    static intersectSnaps(snap1, snap2):Vector2[] {

        const intersections:Vector2[] = [];
        for (let i = 1; i < snap1.points.length; i++) {
            for (let j = 1; j < snap2.points.length; j++) {
                const result = MathUtils.checkLineIntersection(
                    snap1.points[i - 1],
                    snap1.points[i],
                    snap2.points[j - 1],
                    snap2.points[j]
                );

                if (result.onLine1 && result.onLine2) {
                    intersections.push(new Vector2(result.x, result.y));
                }

            }
        }
        return intersections;
    }

    private setGreyOutStateOnPartsConnectedByPortalline(partname, state:GreyOutState) {
        for (let i = 0, len = this.parent.portalLines.length; i < len; i++) {
            if (this.parent.portalLines[i].presentOnPart(partname)) {
                const part = this.parent.modelParts[(this.parent.portalLines[i].getComplimentaryPart(partname) as string)];
                part.setGreyOutState(state);
            }
        }
    }

    /**
     * Alle Parts, die nicht im Moment aktiv sind(d.h das selektierte Objekt ist nicht auf ihnen und sie sind nicht zu einem aktiven Part per PortalLinie verbunden)
     * werden ausgegraut. D.h. im Shader wird am Schluss die Farbne mit Grau gemischt
     */
    greyOutInactiveParts() {
        this.greyoutAllModelParts();

        const activePart = (this.parent.selectedObject as PartSelectableObject).getParent();
        activePart.setGreyOutState(GreyOutState.OUT_OF_DRUCKBEREICH_GREYED_OUT);
        this.setGreyOutStateOnPartsConnectedByPortalline(activePart.name, GreyOutState.OUT_OF_DRUCKBEREICH_GREYED_OUT);
        this.parent.render();
    }

    private greyoutAllModelParts() {
        for (const part in this.parent.modelParts) {
            this.parent.modelParts[part].setGreyOutState(GreyOutState.GREYED_OUT);
        }
    }

    liftGreyout() {
        for (const part in this.parent.modelParts) {
            this.parent.modelParts[part].setGreyOutState(GreyOutState.NOT_GREYED_OUT);
        }
        this.parent.render();
    }

    setNonEmptyLayersVisibleAndToHandleShader() {
        for (let i = 0; i < this.nonEmptyLayers.length; i++) {
            const parent = this.nonEmptyLayers[i].parent as OwayoKonfiPart;
            if (parent.visible) {
                const mesh = parent.mesh as THREE.Mesh;
                mesh.visible = true;
                mesh.material = parent.handleShader;
            }
        }
    }

    public static centerCoordinateToEdge(point:Vector2, widthHeight:WidthHeight, edge:string) {

        const returnObject = new Vector2(point.x, point.y);
        if (edge.indexOf("right") > -1) {
            returnObject.x += widthHeight.width / 2;
        }
        if (edge.indexOf("left") > -1) {
            returnObject.x -= widthHeight.width / 2;
        }
        if (edge.indexOf("bottom") > -1) {
            returnObject.y += widthHeight.height / 2;
        }
        if (edge.indexOf("top") > -1) {
            returnObject.y -= widthHeight.height / 2;
        }
        return returnObject;
    }

    public static edgeCoordinateToCenter(point:Vector2, widthHeight:WidthHeight, edge) {

        const returnObject = new Vector2(point.x, point.y);
        if (edge.indexOf("right") > -1) {
            returnObject.x -= widthHeight.width / 2;
        }
        if (edge.indexOf("left") > -1) {
            returnObject.x += widthHeight.width / 2;
        }
        if (edge.indexOf("bottom") > -1) {
            returnObject.y -= widthHeight.height / 2;
        }
        if (edge.indexOf("top") > -1) {
            returnObject.y += widthHeight.height / 2;
        }
        return returnObject.apply(Math.round)
    }
}
